import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { Drawer } from '@loomstateorg/shared-frontend';
import Button from 'components/commercetools-ui/Button';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';
import { Category } from 'types/category';
import { Reference } from 'types/reference';
import { useAccount } from 'frontastic';
import { NextFrontasticImage } from 'frontastic/lib/image';
import { ImageApi, LogoScale } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/header/types';
import MobileMenu from './content/mobile-menu';
import MobileMenuFooter from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/header/header-navigation/header-navigation-mobile/content/mobile-menu-footer';
import MobileMenuHeader from './content/mobile-menu-header';

export interface Props {
  logo: NextFrontasticImage;
  logoLink: Reference;
  links: Category[];
  src: string;
  logoScale?: LogoScale;
  lazyPermissions?: boolean;
  drawerClassName?: string;
  dashboardDefaultMessage?: string;
  imageApi?: ImageApi;
  children?: React.ReactNode;
}

const HeaderNavigationMobile: FC<Props> = ({
  links,
  logo,
  logoLink,
  src,
  logoScale = '1x',
  lazyPermissions = false,
  drawerClassName = 'w-4/5 border border-neutral-400',
  dashboardDefaultMessage = 'Dashboard',
  imageApi,
}) => {
  const { formatMessage } = useFormat({ name: 'common' });
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const { account, getCustomerGroup, isOrderApprover } = useAccount();
  const [categoriesNavigator, setCategoriesNavigator] = useState<Category[]>([]);
  const [showMenu, setShowMenu] = useState(false);
  const [allowedReports, setAllowedReports] = useState([]);
  const [isApprover, setIsApprover] = useState<boolean | undefined>(lazyPermissions ? undefined : false);

  const showHeaderMenu = () => {
    setShowMenu(true);
  };

  const hideHeaderMenu = () => {
    setShowMenu(false);
    setCategoriesNavigator([]);
  };

  const removeCategory = () => {
    setCategoriesNavigator((array) => array.slice(0, -1));
  };

  const insertCategory = (category: Category) => {
    setCategoriesNavigator((array) => [...array, category]);
  };

  const getAllowedReports = async () => {
    const customerGroup = await getCustomerGroup();
    if (customerGroup) {
      const allowedReports = customerGroup?.customerGroup?.obj?.custom?.fields?.allowedReports || [];
      setAllowedReports(allowedReports);
    }
  };

  const checkIsOrderApprover = async () => {
    setIsApprover(await isOrderApprover());
  };

  const loadPermissions = useCallback(async () => {
    const [customerGroup, approverStatus] = await Promise.all([getCustomerGroup(), isOrderApprover()]);
    if (customerGroup) {
      setAllowedReports(customerGroup?.customerGroup?.obj?.custom?.fields?.allowedReports || []);
    }
    setIsApprover(approverStatus);
  }, [getCustomerGroup, isOrderApprover]);

  useEffect(() => {
    if (lazyPermissions) {
      if (account?.accountId && showMenu && !allowedReports.length && isApprover === undefined) {
        loadPermissions();
      }
      return;
    }
    if (account) {
      getAllowedReports();
      checkIsOrderApprover();
    }
  }, [account, lazyPermissions, showMenu, loadPermissions, allowedReports.length, isApprover]);

  const shouldShowDashboard = allowedReports?.length > 0;

  const accountSubCategories = useMemo(() => {
    const tabs = [
      {
        linkName: formatAccountMessage({ id: 'my.account', defaultMessage: 'My Account' }),
        href: '/account#',
        key: 'account',
      },
      {
        linkName: formatAccountMessage({ id: 'addresses', defaultMessage: 'Addresses' }),
        href: '/account#addresses',
        key: 'addresses',
      },
      {
        linkName: formatAccountMessage({ id: 'orders', defaultMessage: 'My Orders' }),
        href: '/account#orders',
        key: 'orders',
      },
      {
        linkName: formatAccountMessage({ id: 'customer.support', defaultMessage: 'Customer Support' }),
        href: '/account#support',
        key: 'support',
      },
    ] as Array<{ linkName: string; href: string; key: string }>;

    if (!!isApprover)
      tabs.push({
        linkName: formatAccountMessage({ id: 'order-approver', defaultMessage: 'Approval Requests' }),
        href: '/account#order-approver',
        key: 'order-approver',
      });
    if (shouldShowDashboard)
      tabs.push({
        linkName: formatAccountMessage({ id: 'dashboard', defaultMessage: dashboardDefaultMessage }),
        href: '/account#dashboard',
        key: 'dashboard',
      });

    return tabs.map((tab) => ({
      categoryId: tab.href,
      linkName: tab.linkName,
      slug: tab.href,
      subCategories: [],
      depth: 1,
      key: tab.key,
    }));
  }, [formatAccountMessage, isApprover, shouldShowDashboard, dashboardDefaultMessage]);

  return (
    <div className="flex items-stretch lg:hidden" data-testid="mobile-navigation">
      <Button
        variant="ghost"
        size="icon"
        onClick={showHeaderMenu}
        title={formatMessage({ id: 'header.menu.open', defaultMessage: 'Open side menu' })}
        className="mr-8"
        data-testid="icon-menu-item"
      >
        <Bars3Icon className="w-30 lg:w-44" />
      </Button>

      <Drawer
        isOpen={showMenu}
        direction="left"
        className={drawerClassName}
        onClose={hideHeaderMenu}
      >
        <div className="overflow-y-scroll" data-testid="mobile-menu">
          <MobileMenuHeader
            src={src}
            categories={categoriesNavigator}
            hideHeaderMenu={hideHeaderMenu}
            logo={logo}
            logoLink={logoLink}
            logoScale={logoScale}
            imageApi={imageApi}
            onArrowClick={removeCategory}
          />

          <MobileMenu
            links={links}
            hideHeaderMenu={hideHeaderMenu}
            categoriesNavigator={categoriesNavigator}
            insertCategory={insertCategory}
            accountSubCategories={accountSubCategories}
          />

          {categoriesNavigator.length <= 0 && (
            <MobileMenuFooter
              key={`footer-${!!isApprover ? '1' : '0'}-${shouldShowDashboard ? '1' : '0'}`}
              showMenu={showMenu}
              hideHeaderMenu={hideHeaderMenu}
              insertCategory={insertCategory}
              accountSubCategories={accountSubCategories}
            />
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default HeaderNavigationMobile;
